/**
 * Sentry service — no-op-safe wrapper around @sentry/react.
 *
 * All calls are guarded: when VITE_SENTRY_DSN is absent the module is a
 * no-op so the dev environment works with zero env config.
 *
 * ADR-030 constraints:
 * - NO reactRouterV6/V7 integration
 * - tracesSampleRate: 0.2
 * - replaysSessionSampleRate: 0.1
 * - beforeSend/beforeBreadcrumb redact PII keys
 * - setUser receives { id } only — never email, displayName, or plan
 */

import * as Sentry from '@sentry/react';
import { browserTracingIntegration } from '@sentry/react';

const DSN = import.meta.env['VITE_SENTRY_DSN'] as string | undefined;

/** Redacts values whose key matches a PII pattern. */
function redactPii<T extends Record<string, unknown>>(obj: T): T {
  const PII_KEY = /email|displayName|display_name|password|token/i;
  const result: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(obj)) {
    result[key] = PII_KEY.test(key) ? '[Redacted]' : value;
  }
  // CAST: we know the shape matches T after redaction
  return result as T;
}

export const sentryService = {
  init(): void {
    if (!DSN) return;

    Sentry.init({
      dsn: DSN,
      integrations: [browserTracingIntegration()],
      tracesSampleRate: 0.2,
      replaysSessionSampleRate: 0.1,
      replaysOnErrorSampleRate: 1.0,

      beforeSend(event) {
        // Redact PII from extra, request body, and user context
        if (event.extra) {
          event.extra = redactPii(event.extra as Record<string, unknown>);
        }
        if (event.request?.data && typeof event.request.data === 'object') {
          event.request.data = redactPii(event.request.data as Record<string, unknown>);
        }
        if (event.user) {
          // CAST: Sentry User is compatible with Record<string, unknown> for PII redaction
          event.user = redactPii(event.user as unknown as Record<string, unknown>) as typeof event.user;
        }
        return event;
      },

      beforeBreadcrumb(breadcrumb) {
        if (breadcrumb.data) {
          breadcrumb.data = redactPii(breadcrumb.data as Record<string, unknown>);
        }
        return breadcrumb;
      },
    });
  },

  /** Attach user identity to Sentry sessions. Only sends { id } — no email. */
  setUser(user: { id: string } | null): void {
    if (!DSN) return;
    if (user === null) {
      Sentry.setUser(null);
    } else {
      Sentry.setUser({ id: user.id });
    }
  },

  captureException(err: unknown): void {
    if (!DSN) {
      console.error('[Sentry no-op] captureException:', err);
      return;
    }
    Sentry.captureException(err);
  },
};
